import { css } from '@emotion/react'
import { Icon, Text, Theme, useTheme, VFlow } from 'bold-ui'
import { VerticalStep, VerticalStepper } from 'components/vertical-stepper'
import { useFlags } from 'config/useFlagsContext'
import { useMemo } from 'react'

import { MCHAT_PERGUNTAS } from '../model-mchat'

export type MChatModalStepperStatus = 'respondendo' | 'finalizado' | 'finalizado-com-seguimento'

interface MChatModalStepperProps {
  status: MChatModalStepperStatus
  numberOfAnswers: number
}

export function MChatModalStepper(props: Readonly<MChatModalStepperProps>) {
  const { status, numberOfAnswers } = props

  const theme = useTheme()
  const styles = useMemo(() => createStyles(theme, numberOfAnswers), [numberOfAnswers, theme])

  return (
    <VerticalStepper gap={4.5}>
      <VerticalStep
        status={status === 'respondendo' ? 'active' : 'completed'}
        connectorStyle={styles.connector}
        header={
          <VFlow vSpacing={0}>
            <span css={styles.stepTwoConnector} />
            <Text color='primary'>M-CHAT-R&#8482;</Text>
            {status === 'respondendo' && (
              <Text>
                {numberOfAnswers} de {numberOfQuestions} perguntas
              </Text>
            )}
          </VFlow>
        }
      />

      <VerticalStep
        status={status !== 'respondendo' ? 'active' : 'incompleted'}
        overrides={{
          Icon: status === 'respondendo' ? StepTwoIcon : undefined,
        }}
        connectorStyle={css(
          styles.connector,
          status === 'finalizado-com-seguimento' ? styles.stepThreeConnector : styles.stepThreeDashedConnector
        )}
        header={<Text>Resultado e encaminhamento</Text>}
      />

      <VerticalStep
        overrides={{
          Icon: status === 'finalizado-com-seguimento' ? StepThreeIcon : StepThreeDisabledIcon,
        }}
        style={styles.stepThree}
        hasConnector={false}
        header={
          <VFlow vSpacing={0}>
            <Text>Entrevista de seguimento</Text>
            {status === 'finalizado-com-seguimento' && (
              <Text fontStyle='italic' fontWeight='normal'>
                Necessária
              </Text>
            )}
          </VFlow>
        }
      />
    </VerticalStepper>
  )
}

const numberOfQuestions = Object.keys(MCHAT_PERGUNTAS).length

const StepTwoIcon = () => {
  const theme = useTheme()
  const styles = useMemo(() => createStyles(theme), [theme])

  return <div css={styles.stepTwoIcon} />
}

const StepThreeIcon = () => {
  const { PILOTO_M_CHAT_RF_ENABLED } = useFlags()
  return PILOTO_M_CHAT_RF_ENABLED ? (
    <Icon icon='exclamationTriangleFilled' fill='alert' />
  ) : (
    <Icon icon='infoCircleOutline' fill='info' />
  )
}

const StepThreeDisabledIcon = () => {
  const theme = useTheme()
  const styles = useMemo(() => createStyles(theme), [theme])

  return <Icon icon='minusCircleFilled' style={styles.stepThreeDisabledIcon} />
}

const createStyles = (theme: Theme, numberOfAnswers: number = 0) => ({
  stepThree: css`
    margin-bottom: 1rem;
  `,
  stepThreeConnector: css`
    border-color: ${theme.pallete.gray.c60};
  `,
  stepThreeDashedConnector: css`
    border-left-style: dashed;
  `,
  stepThreeDisabledIcon: css`
    fill: ${theme.pallete.gray.c80};
  `,
  stepTwoIcon: css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1rem;
    min-width: 1rem;
    height: 1rem;
    border-radius: 50%;
    background: ${theme.pallete.gray.c60};
  `,
  stepTwoConnector: css`
    width: 0.25rem;
    height: ${(numberOfAnswers * 5.5) / numberOfQuestions}rem;
    background: ${theme.pallete.primary.main};
    position: absolute;
    top: 2rem;
    left: 0.625rem;
    right: 0;
    border-radius: 4px;
    transition: height 0.2s ease;
  `,
  connector: css`
    height: calc(100% - 1.5rem);
    top: 2rem;
    left: 0.625rem;
    border-left-width: 0.25rem;
  `,
})
